import type { Store } from '../net/store'
import type { AudioSettings } from './settings'
import { SFX_IDS, SoundBank } from './sounds'
import type { BufferMaker, Fetcher, FillBuffer, MusicId, SfxId } from './sounds'
import { VoiceLimiter } from './voiceLimiter'

/** Music cross-fades over this long (PHASE3 T3.6). */
export const CROSSFADE_MS = 400
/** After the tab comes back, sounds stay off this long: the catch-up burst plays silently. */
export const QUIET_AFTER_SHOW_MS = 300
/** Debug log length (`window.runkr.audio.log` under `?debug`). */
const LOG_KEEP = 60

/** The parts of the Web Audio API the engine uses, so tests can run on a fake context. */
export interface ParamLike {
  value: number
  setValueAtTime(v: number, t: number): unknown
  linearRampToValueAtTime(v: number, t: number): unknown
  cancelScheduledValues(t: number): unknown
}
export interface NodeLike {
  connect(destination: NodeLike): unknown
  disconnect(): void
}
export interface GainLike extends NodeLike {
  readonly gain: ParamLike
}
export interface SourceLike<B> extends NodeLike {
  buffer: B | null
  loop: boolean
  onended: (() => void) | null
  start(when?: number): void
  stop(when?: number): void
}
export interface ContextLike<B> extends BufferMaker<B> {
  readonly currentTime: number
  readonly state: string
  readonly destination: NodeLike
  createGain(): GainLike
  createBufferSource(): SourceLike<B>
  resume(): Promise<void>
  suspend(): Promise<void>
}

export interface EngineDeps<B> {
  settings: Store<AudioSettings>
  /** Makes the context; called once, on the first user gesture. Null: no audio here. */
  createContext: () => ContextLike<B> | null
  fill: FillBuffer<B>
  fetcher: Fetcher | null
  /** URL of `public/assets/audio/`, with the trailing slash. */
  baseUrl: string
  /** Milliseconds (the limiter's clock). */
  now: () => number
}

/** Bus levels for settings: master (0 when muted), and the music and SFX buses under it. */
export function busGains(s: AudioSettings): { master: number; music: number; sfx: number } {
  return { master: s.muted ? 0 : s.master, music: s.music, sfx: s.sfx }
}

export type DropReason = 'locked' | 'hidden' | 'muted' | 'quiet' | 'limit'

/** What the engine did, for `?debug` and tests. */
export interface AudioStats {
  played: Record<string, number>
  dropped: Record<DropReason, number>
  log: string[]
}

interface Voice<B> {
  src: SourceLike<B>
  gain: GainLike
}

/**
 * Sound for the client (PHASE3 T3.6, D-051): Web Audio directly, no library. The context is
 * made on the first user gesture (`unlock`); before that `play` is dropped, never queued.
 * Sources go through a per-voice gain into the SFX or music bus, both under the master. One
 * music loop at a time, cross-faded; a hidden tab suspends the context (music pauses, SFX off).
 */
export class AudioEngine<B> {
  readonly stats: AudioStats = {
    played: {},
    dropped: { locked: 0, hidden: 0, muted: 0, quiet: 0, limit: 0 },
    log: [],
  }
  private ctx: ContextLike<B> | null = null
  private master: GainLike | null = null
  private sfxBus: GainLike | null = null
  private musicBus: GainLike | null = null
  private bank: SoundBank<B> | null = null
  private readonly limiter = new VoiceLimiter()
  private hidden = false
  private quietUntil = 0
  private wantMusic: MusicId | null = null
  private music: Voice<B> | null = null
  private musicToken = 0

  constructor(private readonly deps: EngineDeps<B>) {}

  get unlocked(): boolean {
    return this.ctx !== null
  }

  /** First user gesture: make the context and buses (later gestures resume a suspended one). */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended' && !this.hidden) void this.ctx.resume().catch(() => {})
      return
    }
    const ctx = this.deps.createContext()
    if (!ctx) return
    this.ctx = ctx
    this.master = ctx.createGain()
    this.sfxBus = ctx.createGain()
    this.musicBus = ctx.createGain()
    this.master.connect(ctx.destination)
    this.sfxBus.connect(this.master)
    this.musicBus.connect(this.master)
    this.applySettings(this.deps.settings.get())
    this.deps.settings.subscribe((s) => this.applySettings(s))
    this.bank = new SoundBank(ctx, this.deps.fill, this.deps.fetcher, this.deps.baseUrl)
    for (const id of SFX_IDS) void this.bank.load(id)
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {})
    if (this.wantMusic) this.switchMusic(this.wantMusic)
  }

  /** Play effect `id` at `gain` (0..1, before the buses). False when it was dropped. */
  play(id: SfxId, gain = 1): boolean {
    const reason = this.dropReason(id)
    if (reason) {
      this.stats.dropped[reason]++
      this.note(`drop ${id} ${reason}`)
      return false
    }
    const ctx = this.ctx!
    const src = ctx.createBufferSource()
    src.buffer = this.bank!.sfx(id)
    const g = ctx.createGain()
    g.gain.value = gain
    src.connect(g)
    g.connect(this.sfxBus!)
    src.onended = () => {
      src.disconnect()
      g.disconnect()
    }
    src.start()
    this.stats.played[id] = (this.stats.played[id] ?? 0) + 1
    this.note(`play ${id} ${gain.toFixed(2)}`)
    return true
  }

  /** Loop music `id` (null: silence), cross-fading from the current one; same id: no change. */
  loop(id: MusicId | null): void {
    if (id === this.wantMusic) return
    this.wantMusic = id
    this.note(`loop ${id ?? 'none'}`)
    if (this.ctx) this.switchMusic(id)
  }

  stopLoop(): void {
    this.loop(null)
  }

  /** The tab was hidden or shown (`visibilitychange`). */
  setHidden(hidden: boolean): void {
    if (hidden === this.hidden) return
    this.hidden = hidden
    if (!hidden) this.quietUntil = this.deps.now() + QUIET_AFTER_SHOW_MS
    const ctx = this.ctx
    if (!ctx) return
    void (hidden ? ctx.suspend() : ctx.resume()).catch(() => {})
  }

  private dropReason(id: SfxId): DropReason | null {
    if (!this.ctx) return 'locked'
    if (this.hidden) return 'hidden'
    if (this.deps.settings.get().muted) return 'muted'
    const now = this.deps.now()
    if (now < this.quietUntil) return 'quiet'
    return this.limiter.allow(id, now) ? null : 'limit'
  }

  private applySettings(s: AudioSettings): void {
    const g = busGains(s)
    if (this.master) this.master.gain.value = g.master
    if (this.sfxBus) this.sfxBus.gain.value = g.sfx
    if (this.musicBus) this.musicBus.gain.value = g.music
  }

  private switchMusic(id: MusicId | null): void {
    const ctx = this.ctx!
    const fade = CROSSFADE_MS / 1000
    const old = this.music
    this.music = null
    if (old) {
      const t = ctx.currentTime
      old.gain.gain.cancelScheduledValues(t)
      old.gain.gain.setValueAtTime(old.gain.gain.value, t)
      old.gain.gain.linearRampToValueAtTime(0, t + fade)
      old.src.stop(t + fade)
    }
    const token = ++this.musicToken
    if (!id) return
    void this.bank!.load(id).then((buffer) => {
      if (token !== this.musicToken || !buffer) return
      const t = ctx.currentTime
      const src = ctx.createBufferSource()
      src.buffer = buffer
      src.loop = true
      const g = ctx.createGain()
      g.gain.setValueAtTime(0, t)
      g.gain.linearRampToValueAtTime(1, t + fade)
      src.connect(g)
      g.connect(this.musicBus!)
      src.onended = () => {
        src.disconnect()
        g.disconnect()
      }
      src.start()
      this.music = { src, gain: g }
    })
  }

  private note(line: string): void {
    this.stats.log.push(line)
    if (this.stats.log.length > LOG_KEEP) this.stats.log.shift()
  }
}
